import React from 'react';
import {
  Box,
  Text,
  Input,
  Button,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useBreakpointValue,
} from '@chakra-ui/react';

const CATEGORY_LABELS = {
  profession: 'Professioni',
  hobby: 'Hobby',
  skill: 'Competenze',
};

const CATEGORY_HINTS = {
  profession: 'Cosa fai nella vita o nel lavoro',
  hobby: 'Passioni e interessi',
  skill: 'Cosa sai fare bene',
};

const TagCategoryBlock = ({
  category,
  allTags,
  currentDraft,
  newTagNames,
  onToggleTag,
  onNewTagNameChange,
  onAddTag,
}) => {
  const categoryTags = allTags.filter((t) => t.category === category);
  const selectedIds = currentDraft?.tagIds || [];
  const selectedTags = categoryTags.filter((t) => selectedIds.includes(t.id));
  const unselectedTags = categoryTags.filter(
    (t) => !selectedIds.includes(t.id)
  );
  const inputValue = newTagNames[category] || '';
  const searchLower = inputValue.trim().toLowerCase();
  const filteredUnselected =
    searchLower.length > 0
      ? unselectedTags.filter(
          (t) => t.name && t.name.toLowerCase().includes(searchLower)
        )
      : unselectedTags;
  const label = CATEGORY_LABELS[category];
  const hint = CATEGORY_HINTS[category];

  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={3}>
        {hint}
      </Text>

      {selectedTags.length > 0 && (
        <HStack spacing={2} wrap="wrap" mb={3}>
          {selectedTags.map((tag) => (
            <Tag
              key={tag.id}
              size="md"
              variant="solid"
              bg="yellow.400"
              color="black"
              cursor="pointer"
              onClick={() => onToggleTag(tag.id)}
            >
              <TagLabel>{tag.name}</TagLabel>
              <TagCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleTag(tag.id);
                }}
              />
            </Tag>
          ))}
        </HStack>
      )}

      <HStack
        spacing={2}
        align="stretch"
        flexDirection={{ base: 'column', sm: 'row' }}
      >
        <Input
          flex="1"
          value={inputValue}
          onChange={(e) => onNewTagNameChange(category, e.target.value)}
          placeholder={`Cerca o crea ${label.toLowerCase()}…`}
          size="md"
          bg="#202225"
          borderColor="gray.600"
          _hover={{ borderColor: 'gray.500' }}
          _focus={{
            borderColor: 'yellow.400',
            boxShadow: '0 0 0 1px #ECC94B',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAddTag(category);
            }
          }}
        />
        <Button
          size="md"
          onClick={() => onAddTag(category)}
          colorScheme="yellow"
          color="black"
          flexShrink={0}
        >
          Aggiungi
        </Button>
      </HStack>

      {searchLower.length > 0 && (
        <Box mt={3}>
          {filteredUnselected.length > 0 ? (
            <HStack spacing={2} wrap="wrap">
              {filteredUnselected.map((tag) => (
                <Tag
                  key={tag.id}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  cursor="pointer"
                  onClick={() => {
                    onToggleTag(tag.id);
                    onNewTagNameChange(category, '');
                  }}
                >
                  <TagLabel>+ {tag.name}</TagLabel>
                </Tag>
              ))}
            </HStack>
          ) : (
            <Text fontSize="xs" color="gray.500">
              Nessun tag trovato — premi Aggiungi per crearne uno nuovo.
            </Text>
          )}
        </Box>
      )}

      {searchLower.length === 0 &&
        unselectedTags.length > 0 &&
        selectedTags.length === 0 && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Suggerimenti popolari
            </Text>
            <HStack spacing={2} wrap="wrap">
              {unselectedTags.slice(0, 8).map((tag) => (
                <Tag
                  key={tag.id}
                  size="sm"
                  variant="subtle"
                  colorScheme="gray"
                  cursor="pointer"
                  onClick={() => onToggleTag(tag.id)}
                >
                  <TagLabel>+ {tag.name}</TagLabel>
                </Tag>
              ))}
            </HStack>
          </Box>
        )}
    </Box>
  );
};

const TagsSection = ({
  allTags,
  tagsLoading,
  currentDraft,
  newTagNames,
  onToggleTag,
  onNewTagNameChange,
  onAddTag,
}) => {
  const useTabs = useBreakpointValue({ base: true, lg: false });

  if (tagsLoading) {
    return (
      <Text fontSize="sm" color="gray.400">
        Caricamento tag…
      </Text>
    );
  }

  const categories = ['profession', 'hobby', 'skill'];
  const selectedCount = (currentDraft?.tagIds || []).length;

  const renderCategory = (category) => (
    <TagCategoryBlock
      key={category}
      category={category}
      allTags={allTags}
      currentDraft={currentDraft}
      newTagNames={newTagNames}
      onToggleTag={onToggleTag}
      onNewTagNameChange={onNewTagNameChange}
      onAddTag={onAddTag}
    />
  );

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" color="gray.400">
        Scegli tag che ti rappresentano. Ne hai selezionati:{' '}
        <Text as="span" color="yellow.400" fontWeight="semibold">
          {selectedCount}
        </Text>
      </Text>

      {useTabs ? (
        <Tabs variant="soft-rounded" colorScheme="yellow" isFitted size="sm">
          <TabList bg="gray.800" p={1} borderRadius="lg">
            {categories.map((cat) => (
              <Tab
                key={cat}
                color="gray.300"
                _selected={{ bg: 'yellow.400', color: 'black' }}
                fontSize="xs"
                px={2}
              >
                {CATEGORY_LABELS[cat]}
              </Tab>
            ))}
          </TabList>
          <TabPanels pt={4}>
            {categories.map((cat) => (
              <TabPanel key={cat} px={0} py={0}>
                {renderCategory(cat)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      ) : (
        categories.map((cat) => (
          <Box
            key={cat}
            p={4}
            borderRadius="lg"
            bg="gray.800"
            borderWidth="1px"
            borderColor="gray.600"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.200" mb={2}>
              {CATEGORY_LABELS[cat]}
            </Text>
            {renderCategory(cat)}
          </Box>
        ))
      )}
    </VStack>
  );
};

export default TagsSection;
